import dayjs, { Dayjs } from 'dayjs';
import { HCP_CUBE_NAME, LOCATION_CUBE_NAME } from '../../api/hcp/interaction/types';
import { HCP, HCPBase, HCPExtended } from '../../models';
import { CubeRawRow } from './mapper.type';

const splitSemicolonList = (value: string | null): string[] => {
  if (typeof value !== 'string') return [];
  return value.split(';').reduce<string[]>((acc, item) => {
    const trimmed = item.trim();
    if (trimmed) acc.push(trimmed);
    return acc;
  }, []);
};

/**
 * @deprecated Use toStringOrNull instead for better handling of empty strings and null/undefined values.
 */
export const toString = (data: string | number | boolean): string => (data ? (data as string) : null);

/**
 * Converts a value to a string, returning null for empty strings or if the value is null/undefined.
 * If a fallback value is provided, it will return the fallback instead of null.
 * @param data The value to convert to a string.
 * @param fallback An optional fallback value to return if the input is null, undefined, or an empty string.
 * @returns The string representation of the input value, the fallback value, or null.
 */
export function toStringOrNull(data: unknown): string | null;
export function toStringOrNull(data: unknown, fallback: string): string;

export function toStringOrNull(data: unknown, fallback?: string) {
  if (data === null || data === undefined || typeof data === 'object') {
    return fallback ?? null;
  }

  if (typeof data === 'string') {
    const trimmed = data.trim();
    return trimmed !== '' ? trimmed : (fallback ?? null);
  }

  return String(data);
}

export const toBoolean = (data: string | number | boolean): boolean | undefined => {
  const value = data?.toString();
  return value?.startsWith('1') ? true : value?.startsWith('0') ? false : undefined;
};

export const toDate = (data: string | number | boolean): Dayjs => {
  return data ? dayjs(data as string) : null;
};

/**
 * @deprecated Use toNumberOrNull instead for better handling of non-numeric values and null/undefined values.
 */
export const toNumber = (data: string | number | boolean): number => {
  if (data === false || data === 0) return 0;
  return data ? +data : null;
};

/**
 * Converts a value to a number, returning null for non-numeric values or if the value is null/undefined.
 * If the value is false or 0, it will return 0.
 * @param data The value to convert to a number.
 * @returns The numeric representation of the input value, or null if the value is non-numeric or null/undefined.
 */
export const toNumberOrNull = (value: unknown): number | null => {
  if (value === null || value === undefined) return null;

  if (Array.isArray(value)) return null;

  if (typeof value === 'string') {
    const trimmed = value.trim();
    if (trimmed === '') return null;

    const num = Number(trimmed);
    return Number.isFinite(num) ? num : null;
  }

  const num = Number(value);
  return Number.isFinite(num) ? num : null;
};

export const parseTaeRecords = (taeJsonString = '[]') => {
  try {
    return JSON.parse(taeJsonString).map((item: { product_name: string; tae: string }) => ({
      productName: item.product_name,
      tae: item.tae,
    }));
  } catch (e) {
    return [];
  }
};

export const parseClassificationLevels = (classificationJsonString = '[]') => {
  try {
    return JSON.parse(classificationJsonString).map((item: { product_name: string; classification: string }) => ({
      productName: item.product_name,
      classification: item.classification,
    }));
  } catch (e) {
    return [];
  }
};

const HCP_VIEW = 'hcp_view';

const mapHCPBase = (namespace: string, row: CubeRawRow): HCPBase => ({
  id: toString(row[`${namespace}.uci`]),
  bk: toString(row[`${namespace}.hcp_bk`]),
  uci: toString(row[`${namespace}.uci`]),
  contact: {
    emails: splitSemicolonList(toString(row[`${namespace}.email_address_hcp_view`])),
    phoneNumbers: splitSemicolonList(toString(row[`${namespace}.phone_number_hcp_view`])),
    linkedIn: toString(row[`${namespace}.linkedin_url`]),
  },
  person: {
    fullName: toString(row[`${namespace}.hcp_full_name_backward`]),
    titles: toString(row[`${namespace}.job_title`])?.split(',') ?? [],
    age: toNumber(row[`${namespace}.age`]),
  },
  speciality: toString(row[`${namespace}.specialty`]),
  country: toString(row[`${namespace}.country_name`]),
  title: toString(row[`${namespace}.title`]),
  classification:
    toString(row[`${namespace}.classification`])
      ?.split(',')
      ?.map(x => x.trim()) ?? [],
  type: toString(row[`${namespace}.type`]),
  nationalId: toString(row[`${namespace}.national_id`]),
  hcp_consent_profiling_business_relation_flag: toString(
    row[`${namespace}.hcp_consent_profiling_business_relation_flag`],
  ),

  hcp_consent_profiling_communication_flag: toString(row[`${namespace}.hcp_consent_profiling_communication_flag`]),
  consent_disclosure: toString(row[`${namespace}.consent_disclosure`]),
  consent_do_not_call: toString(row[`${HCP_VIEW}.consent_do_not_call`]),
  hcp_mdm_id: toString(row[`${HCP_VIEW}.hcp_mdm_id`]),
  primaryPointOfContact: toString(row[`${HCP_VIEW}.primary_point_of_contact`]),
  primaryPointOfContactEmail: toString(row[`${HCP_VIEW}.primary_point_of_contact_email`]),
  hcpRegistrationCompleteFlag: Boolean(row[`${HCP_VIEW}.hcp_registration_complete_flag`]),
});

const mapRowData = (row: CubeRawRow) => {
  return {
    tae: parseTaeRecords(row[`${HCP_CUBE_NAME}.tae_json`] as string),
    classification: parseClassificationLevels(row[`${HCP_CUBE_NAME}.classification_json`] as string),
  };
};

export const mapHCPView = (row: CubeRawRow): HCPExtended => ({
  ...mapHCPBase(HCP_VIEW, row),
  crmLink: toString(row[`${HCP_VIEW}.crm_link`]),
  consents: {
    email: toNumber(row[`${HCP_VIEW}.consent_email_consent_status`]),
    phone: toNumber(row[`${HCP_VIEW}.consent_call_consent_status`]),
    sms: toNumber(row[`${HCP_VIEW}.consent_sms_consent_status`]),
    fax: toNumber(row[`${HCP_VIEW}.consent_fax_consent_status`]),
  },
  donts: {
    consentDontCall: row[`${HCP_VIEW}.consent_do_not_call`],
    consentDontFax: row[`${HCP_VIEW}.consent_do_not_fax`],
    consentDontMail: row[`${HCP_VIEW}.consent_do_not_mail`],
    consentDontVisit: row[`${HCP_VIEW}.consent_do_not_visit`],
    consentDontEmail: row[`${HCP_VIEW}.consent_do_not_email`],
    consentDontPhone: row[`${HCP_VIEW}.consent_do_not_phone`],
    consentDontRemoteCall: row[`${HCP_VIEW}.consent_do_not_remote_call`],
  },
});
export const mapHCP = (row: CubeRawRow): HCP => ({
  ...mapHCPBase(HCP_CUBE_NAME, row),
  ...mapRowData(row),
  countryCode: toStringOrNull(row[`${HCP_CUBE_NAME}.country_code`]),
  latestInteraction: toDate(row[`${HCP_CUBE_NAME}.latest_interaction`]),
  accountPlanInProgressCount: toNumberOrNull(row[`${HCP_CUBE_NAME}.account_plan_in_progress_count`]),
  primaryLocation: {
    name: toString(row[`${LOCATION_CUBE_NAME}.location_name`]),
    location: {
      id: toString(row[`${LOCATION_CUBE_NAME}.id`]),
      primary: true,
      city: toString(row[`${LOCATION_CUBE_NAME}.city`]),
      country: toString(row[`${LOCATION_CUBE_NAME}.country_name`]),
      latitude: toNumberOrNull(row[`${LOCATION_CUBE_NAME}.hco_address_geolocation_latitude`]),
      longitude: toNumberOrNull(row[`${LOCATION_CUBE_NAME}.hco_address_geolocation_longitude`]),
    },
    jobTitle: toString(row[`${HCP_CUBE_NAME}.job_roles`])?.replace(/,/g, ', '),
    crmLink: toString(row[`${HCP_CUBE_NAME}.hco_crm_link`]),
  },
});

export const mapNullToDash = (value: string | null | undefined) => {
  if (value == null || value === 'null') {
    return '-';
  }

  return value;
};
